/**
 * ClinicLocationsCard — manages the physical branches of a multi-clinic tenant.
 *
 * Solo-mode tenants see a CTA to enable multi-location mode. Multi-mode
 * tenants get the full add/edit/remove UI. All operations are scoped by
 * clinic_id on the backend.
 */
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Building2, MapPin, Phone, Mail, Clock4, Plus, Trash2, Pencil, X, CheckCircle2, Star } from 'lucide-react';
import { safeConfirm } from '../../lib/confirm';

export default function ClinicLocationsCard({ enabled, onEnable }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | 'new' | location_id
  const [draft, setDraft] = useState({ name: '', address: '', phone: '', email: '', hours: '', is_primary: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clinic/locations');
      setLocations(data || []);
    } catch (_) { setLocations([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (enabled) reload(); else setLoading(false); }, [enabled]);

  const startNew = () => {
    setDraft({ name: '', address: '', phone: '', email: '', hours: '', is_primary: locations.length === 0 });
    setEditing('new');
    setErr('');
  };
  const startEdit = (loc) => {
    setDraft({ name: loc.name || '', address: loc.address || '', phone: loc.phone || '', email: loc.email || '', hours: loc.hours || '', is_primary: !!loc.is_primary });
    setEditing(loc.location_id);
    setErr('');
  };
  const cancel = () => { setEditing(null); setErr(''); };

  const save = async () => {
    if (!draft.name.trim()) { setErr('Location name is required'); return; }
    setBusy(true); setErr('');
    try {
      if (editing === 'new') {
        await api.post('/clinic/locations', draft);
      } else {
        await api.put(`/clinic/locations/${editing}`, draft);
      }
      await reload();
      setEditing(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (loc) => {
    if (!await safeConfirm(`Remove "${loc.name}"? This is a soft delete — historical records keep this location.`)) return;
    try {
      await api.delete(`/clinic/locations/${loc.location_id}`);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="admin-card md:col-span-2" data-testid="locations-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Building2 className="w-4 h-4 text-brand"/>
        <h3 className="font-heading font-semibold">Clinic locations</h3>
        {enabled && !loading && (
          <span className="text-[10px] uppercase tracking-wider bg-brand/10 text-brand px-1.5 py-0.5 rounded font-medium">
            {locations.length} {locations.length === 1 ? 'branch' : 'branches'}
          </span>
        )}
      </div>

      {!enabled ? (
        <div className="surface border border-dashed border-soft rounded-lg p-6 text-center" data-testid="locations-disabled">
          <Building2 className="w-8 h-8 text-fg-muted mx-auto"/>
          <h4 className="font-heading font-semibold mt-3">You're in Solo clinic mode</h4>
          <p className="text-xs text-fg-muted mt-1 max-w-md mx-auto">
            Solo tenants run from one location and don't need a branch list. Enable Multi-clinic mode
            in the Clinic mode card above to add additional locations.
          </p>
          {onEnable && (
            <button onClick={onEnable} className="btn-primary text-sm mt-4 inline-flex items-center gap-1.5" data-testid="locations-upgrade-btn">
              <Plus className="w-3.5 h-3.5"/> Switch to Multi-clinic mode
            </button>
          )}
        </div>
      ) : loading ? (
        <div className="text-xs text-fg-muted">Loading branches…</div>
      ) : (
        <>
          {locations.length === 0 && editing !== 'new' && (
            <div className="surface border border-soft rounded-lg p-4 text-sm text-fg-muted text-center">
              No branches yet. Add your first one below.
            </div>
          )}
          <div className="space-y-2">
            {locations.map((loc) => editing === loc.location_id ? (
              <EditRow key={loc.location_id} draft={draft} setDraft={setDraft} save={save} cancel={cancel} busy={busy} err={err}/>
            ) : (
              <div key={loc.location_id} className="surface border border-soft rounded-lg p-3 flex items-start gap-3 group" data-testid={`location-${loc.location_id}`}>
                <div className="w-9 h-9 rounded-full bg-brand/10 flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-brand"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold inline-flex items-center gap-1.5">
                    {loc.name}
                    {loc.is_primary && <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider bg-[#FFB000] text-[#0F1B2D] px-1.5 py-0.5 rounded font-medium"><Star className="w-2.5 h-2.5 fill-current"/> Primary</span>}
                  </div>
                  <div className="text-xs text-fg-muted mt-1 space-y-0.5">
                    {loc.address && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3"/> {loc.address}</div>}
                    {loc.phone   && <div className="flex items-center gap-1.5"><Phone   className="w-3 h-3"/> {loc.phone}</div>}
                    {loc.email   && <div className="flex items-center gap-1.5"><Mail    className="w-3 h-3"/> {loc.email}</div>}
                    {loc.hours   && <div className="flex items-center gap-1.5"><Clock4  className="w-3 h-3"/> {loc.hours}</div>}
                  </div>
                </div>
                <div className="flex flex-col gap-1 opacity-60 group-hover:opacity-100 transition">
                  <button onClick={()=>startEdit(loc)} className="text-fg-muted hover:text-brand p-1" data-testid={`edit-loc-${loc.location_id}`} title="Edit">
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                  <button onClick={()=>remove(loc)} className="text-fg-muted hover:text-[#B83A3A] p-1" data-testid={`del-loc-${loc.location_id}`} title="Remove">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
              </div>
            ))}
            {editing === 'new' && (
              <EditRow draft={draft} setDraft={setDraft} save={save} cancel={cancel} busy={busy} err={err} isNew/>
            )}
          </div>
          {editing === null && (
            <button onClick={startNew} className="btn-secondary text-sm mt-3 inline-flex items-center gap-1.5" data-testid="add-location-btn">
              <Plus className="w-3.5 h-3.5"/> Add branch
            </button>
          )}
        </>
      )}
    </div>
  );
}

function EditRow({ draft, setDraft, save, cancel, busy, err, isNew = false }) {
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  return (
    <div className="surface border border-brand rounded-lg p-4 space-y-2.5 bg-brand/5" data-testid={isNew ? 'location-new-row' : 'location-edit-row'}>
      <div className="grid sm:grid-cols-2 gap-2.5">
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted mb-1">Branch name *</div>
          <input className="input w-full" value={draft.name} onChange={e=>set('name', e.target.value)} placeholder="Warsaw Centrum"
                 data-testid="loc-name" autoFocus/>
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted mb-1">Phone</div>
          <input className="input w-full" value={draft.phone} onChange={e=>set('phone', e.target.value)} placeholder="+48 22 …"
                 data-testid="loc-phone"/>
        </label>
        <label className="block sm:col-span-2">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted mb-1">Address</div>
          <input className="input w-full" value={draft.address} onChange={e=>set('address', e.target.value)} placeholder="ul. Marszałkowska 1, 00-001 Warszawa"
                 data-testid="loc-address"/>
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted mb-1">Email</div>
          <input className="input w-full" type="email" value={draft.email} onChange={e=>set('email', e.target.value)} placeholder="warsaw@yourclinic.com"
                 data-testid="loc-email"/>
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted mb-1">Opening hours</div>
          <input className="input w-full" value={draft.hours} onChange={e=>set('hours', e.target.value)} placeholder="Mon–Fri 9:00–18:00"
                 data-testid="loc-hours"/>
        </label>
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
        <input type="checkbox" checked={!!draft.is_primary} onChange={e=>set('is_primary', e.target.checked)}
               className="accent-[var(--brand)]" data-testid="loc-primary"/>
        <span>Primary location <span className="text-fg-muted text-xs">(used on invoices + booking confirmations)</span></span>
      </label>
      {err && <div className="text-xs text-[#B83A3A]" data-testid="loc-err">{err}</div>}
      <div className="flex justify-end gap-2">
        <button onClick={cancel} className="btn-secondary text-sm inline-flex items-center gap-1.5"><X className="w-3.5 h-3.5"/> Cancel</button>
        <button onClick={save} disabled={busy || !draft.name.trim()} className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="loc-save">
          <CheckCircle2 className="w-3.5 h-3.5"/> {busy ? 'Saving…' : (isNew ? 'Add branch' : 'Save')}
        </button>
      </div>
    </div>
  );
}
